import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import Text from 'components/_UI/Text';
import establishmentsApi from 'api/requests/establishments';
import eventsApi from 'api/requests/events';
import paths from 'routes/paths';
import { useCurrentLocation } from 'context/user/CurrentLocation';
import { useEstablishmentsList } from 'context/establishments/EstablishmentsList';
import { useEstablishmentFilter } from 'context/establishments/EstablishmentFilter';
import EstablishmentsNearToYou from './nearToYou/EstablishmentsNearToYou';
import EstablishmentFeaturesSection from './features/EstablishmentFeaturesSection';
import { ui } from 'theme/discover';
import CitiesList from 'modules/home/CitiesList';
import EstablishmentCarousel from './carousel/EstablishmentCarousel';
import CarouselSkeleton from 'components/commons/skeletons/CarouselSkeleton';
import { useNavigate } from 'react-router-dom';

export default function EstablishmentsV2({
    onLoadData,
    onLoading,
}) {
    const { currentLocation, setCurrentLocation } = useCurrentLocation();
    const { setEstablishmentsList } = useEstablishmentsList();
    const { establishmentFilter } = useEstablishmentFilter();
    const isFirstFilterRender = useRef(true);
    const [establishmentData, setEstablishmentData] = useState();
    const [activeLocations, setActiveLocations] = useState([]);
    const navigate = useNavigate();

    const [features, setFeatures] = useState([]);

    useEffect(() => {
        eventsApi.getActiveLocations()
            .then(({ data }) => setActiveLocations(data?.states || []))
            .catch(() => {});

        // Mesma fonte do app (/establishments/options): /features responde 500.
        establishmentsApi.getOptions()
            .then(({ data }) => setFeatures(data?.features || []))
            .catch(() => setFeatures([]));
    }, []);

    function getStateForCity(cityName) {
        for (const state of activeLocations) {
            if (state.cities?.some(c => c.name === cityName)) return state.code;
        }
        return '';
    }

    useEffect(() => {
        getEstablishments();
    }, [currentLocation?.city])

    useEffect(() => {
        if (isFirstFilterRender.current) {
            isFirstFilterRender.current = false;
            return;
        }
        if (establishmentFilter) {
            getFilteredEstablishments(establishmentFilter);
        } else {
            getEstablishments();
        }
    }, [establishmentFilter])

    async function getEstablishments() {
        onLoading(true);
        try {
            const { data } = await establishmentsApi.getForMap();
            const arraysWithMultiple = data?.data?.filter(obj => obj.hasOwnProperty('multiples'));
            const arraysWithoutMultiple = data?.data?.filter(obj => {
                return !obj.hasOwnProperty('multiples') && !obj.hasOwnProperty('noAddress');
            });

            const formatedObject = {
                data: arraysWithoutMultiple,
                withMultiples: arraysWithMultiple
            };

            onLoadData(formatedObject);
            setEstablishmentsList(formatedObject);
            setEstablishmentData(formatedObject);
        } catch (error) {
            console.error('Error fetching establishments:', error);
            setEstablishmentsList({ data: [], withMultiples: [] });
            setEstablishmentData({ data: [], withMultiples: [] });
        } finally {
            onLoading(false);
        }
    }

    async function getFilteredEstablishments(filter) {
        if (!currentLocation?.city) return;
        onLoading(true);
        try {
            const { data } = await establishmentsApi.getForMap(currentLocation.city, filter);
            const arraysWithMultiple = data?.data?.filter(obj => obj.hasOwnProperty('multiples'));
            const arraysWithoutMultiple = data?.data?.filter(obj => {
                return !obj.hasOwnProperty('multiples') && !obj.hasOwnProperty('noAddress');
            });

            const formatedObject = {
                data: arraysWithoutMultiple,
                withMultiples: arraysWithMultiple
            };

            onLoadData(formatedObject);
            setEstablishmentsList(formatedObject);
            setEstablishmentData(formatedObject);
        } catch (error) {
            console.error('Error fetching filtered establishments:', error);
            setEstablishmentsList({ data: [], withMultiples: [] });
            setEstablishmentData({ data: [], withMultiples: [] });
        } finally {
            onLoading(false);
        }
    }

    return (
        <>
            <EstablishmentsContainer>
                {establishmentData === undefined ? (
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Em destaque</SectionTitle>
                        </SectionHeader>
                        <CarouselSkeleton />
                    </Section>
                ) : establishmentData?.data?.length > 0 ? (
                    <Section>
                        <SectionHeader>
                            <SectionTitle>
                                {currentLocation?.city ? `Em destaque em ${currentLocation.city}` : 'Em destaque'}
                            </SectionTitle>
                        </SectionHeader>
                        <EstablishmentCarousel
                            data={establishmentData.data.slice(0, 12)}
                            onSlideClick={(establishment) => navigate(`/estabelecimento/${establishment.slug}/detalhes`)}
                        />
                    </Section>
                ) : null}

                <Section>
                    <SectionHeader>
                        <SectionTitle>Perto de você</SectionTitle>
                        <SeeAllLink type='button' onClick={() => {
                            if (currentLocation?.city) {
                                const stateCode = currentLocation.state || getStateForCity(currentLocation.city);
                                const citySlug = [currentLocation.city, stateCode]
                                    .filter(Boolean).join('-').toLowerCase().replace(/\s+/g, '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                                navigate(`${paths.establishments.search}?city=${encodeURIComponent(citySlug)}`);
                            } else {
                                navigate(paths.establishments.search);
                            }
                        }}>
                            Ver tudo
                        </SeeAllLink>
                    </SectionHeader>
                    <EstablishmentsNearToYou
                        onCardClick={(establishment) => navigate(`/estabelecimento/${establishment.slug}/detalhes`)}
                        onEventClick={() => {}}
                        searchEvents={() => {}}
                    />
                </Section>

                {/* As categorias viraram as pílulas do topo da home, como no app. */}

                {features.length > 0 && (
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Características do local</SectionTitle>
                        </SectionHeader>
                        <EstablishmentFeaturesSection
                            features={features}
                            onFeatureClick={(feature) => navigate(`${paths.establishments.search}?features=${feature.id}`)}
                        />
                    </Section>
                )}

                <Section>
                    <SectionHeader>
                        <SectionTitle>O que fazer por aí?</SectionTitle>
                    </SectionHeader>
                    <CitiesList />
                </Section>
            </EstablishmentsContainer>

        </>
    )
}

const EstablishmentsContainer = styled(Column)`
    width: 100%;
    height: auto;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 100%;
        display: flex;
        margin-top: 20px;
    }
`;

// Seção da home no desenho do app (.msection): título à esquerda, "Ver tudo" à direita.
const Section = styled.section`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 34px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    line-height: 1.3;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${ui.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const SeeAllLink = styled.button`
    flex-shrink: 0;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 13.5px;
    font-weight: 700;
    color: ${ui.link};
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
`;
